import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Page, NotifyModal, ModalAlert } from 'components';
import { Form } from './components';
import Header from 'components/Header/Header';
import {
  createAssistencia,
  getAssistenciaById,
  updateAssistencia,
} from '../Assistencias/assistenciasApi';
import {
  EMPTY_ASSISTENCIA_FORM,
  assistenciaToForm,
  buildAssistenciaFormData,
  validateAssistenciaImageFile,
} from '../Assistencias/assistenciasFormShared';

const AssistenciasAdd = () => {
  const history = useHistory();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const user = JSON.parse(localStorage.getItem('userData') || 'null');
  if (user?.permission?.assistencias?.write !== true) {
    window.location.replace('/home');
  }

  const [assistencia, setAssistencia] = useState({ ...EMPTY_ASSISTENCIA_FORM });
  const [type, setType] = useState({ color: '', status: '' });
  const [modalShow, setModalShow] = useState(false);
  const [showModalAlert, setShowModalAlert] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [imageFiles, setImageFiles] = useState({ logo: null, assinatura: null });
  const [clearedImages, setClearedImages] = useState({ logo: false, assinatura: false });

  const pageTitle = isEdit ? 'Editar Assistência' : 'Adicionar Assistências';
  const headerPage = isEdit ? 'Editar Assistência' : 'Adicionar Assistências';

  useEffect(() => {
    if (!isEdit || !id) {
      setAssistencia({ ...EMPTY_ASSISTENCIA_FORM });
      setImageFiles({ logo: null, assinatura: null });
      setClearedImages({ logo: false, assinatura: false });
      setLoading(false);
      return;
    }

    const loadAssistencia = async () => {
      try {
        setLoading(true);
        setLoadError('');
        const data = await getAssistenciaById(id);

        if (!data) {
          setLoadError('Assistência não encontrada.');
          return;
        }

        setAssistencia(assistenciaToForm(data));
        setImageFiles({ logo: null, assinatura: null });
        setClearedImages({ logo: false, assinatura: false });
      } catch (error) {
        console.error('Erro ao carregar assistência:', error);
        setLoadError('Não foi possível carregar os dados da assistência.');
      } finally {
        setLoading(false);
      }
    };

    loadAssistencia();
  }, [id, isEdit]);

  const handleModalAlert = (msg) => {
    setMessage(msg);
    setShowModalAlert(true);
  };

  const handleAlertModalSuccess = (msg) => {
    setType({ color: '#198754', status: msg });
    setModalShow(true);
  };

  const handleAlertModalError = (msg) => {
    setType({ color: 'red', status: msg });
    setModalShow(true);
  };

  const handleFieldChange = (event, field, value) => {
    event.persist && event.persist();
    setAssistencia((values) => ({
      ...values,
      [field]: value,
    }));
  };

  const handleImageFileSelect = (fieldKey, file) => {
    const error = validateAssistenciaImageFile(file);
    if (error) {
      handleModalAlert(error);
      return;
    }
    setImageFiles((prev) => ({ ...prev, [fieldKey]: file }));
    setClearedImages((prev) => ({ ...prev, [fieldKey]: false }));
  };

  const handleImageFileClear = (fieldKey) => {
    setImageFiles((prev) => ({ ...prev, [fieldKey]: null }));
    setClearedImages((prev) => ({ ...prev, [fieldKey]: true }));
    setAssistencia((values) => ({ ...values, [fieldKey]: '' }));
  };

  const getApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data) return data;
    if (typeof data?.message === 'string' && data.message) return data.message;
    if (typeof data?.error === 'string' && data.error) return data.error;
    return fallback;
  };

  const validateForm = () => {
    if (!assistencia.nome?.trim()) {
      handleModalAlert('Preencha o campo "Nome da empresa".');
      return false;
    }
    if (!assistencia.cidade?.trim()) {
      handleModalAlert('Preencha o campo "Cidade".');
      return false;
    }
    if (!assistencia.estado?.trim()) {
      handleModalAlert('Preencha o campo "Estado".');
      return false;
    }
    if (!assistencia.email?.trim()) {
      handleModalAlert('Preencha o campo "Email principal".');
      return false;
    }
    if (assistencia.valor === '' || assistencia.valor === null || assistencia.valor === undefined) {
      handleModalAlert('Preencha o campo "Chave Assist.".');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const formData = buildAssistenciaFormData(assistencia, {
      logoFile: imageFiles.logo,
      assinaturaFile: imageFiles.assinatura,
    });

    try {
      setSaving(true);

      if (isEdit) {
        const updated = await updateAssistencia(id, formData);
        handleAlertModalSuccess('Atualizado com sucesso!');
        if (updated?.logo || updated?.assinatura) {
          setAssistencia(assistenciaToForm(updated));
        }
        setImageFiles({ logo: null, assinatura: null });
        setTimeout(() => {
          history.push(`/assistencias/${id}`);
        }, 1200);
      } else {
        const created = await createAssistencia(formData);
        handleAlertModalSuccess('Adicionado com sucesso!');
        setAssistencia({ ...EMPTY_ASSISTENCIA_FORM });
        setImageFiles({ logo: null, assinatura: null });
        setTimeout(() => {
          const newId = created?._id;
          if (newId) {
            history.push(`/assistencias/${newId}`);
          } else {
            history.push('/assistencias');
          }
        }, 1200);
      }
    } catch (error) {
      console.error('Erro ao salvar assistência:', error);
      const status = error?.response?.status;
      const fallback = isEdit ? 'Falha ao atualizar!' : 'Falha ao adicionar!';
      if (status === 409) {
        handleAlertModalError(getApiErrorMessage(error, 'E-mail já cadastrado.'));
      } else if (status === 400) {
        handleAlertModalError(getApiErrorMessage(error, 'Dados ou imagem inválidos.'));
      } else {
        handleAlertModalError(getApiErrorMessage(error, fallback));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <Page className="p-3" title={pageTitle}>
        <Header page={headerPage} />
        <div className="text-center text-muted mt-5">
          <p>{loadError}</p>
          <button type="button" className="btn btn-secondary" onClick={() => history.push('/assistencias')}>
            Voltar
          </button>
        </div>
      </Page>
    );
  }

  return (
    <Page className="p-3" title={pageTitle}>
      <Header page={headerPage} />

      <ModalAlert
        message={message}
        showModalAlert={showModalAlert}
        onHide={() => setShowModalAlert(false)}
      />

      <NotifyModal
        type={type}
        show={modalShow}
        onHide={() => setModalShow(false)}
      />

      <Form
        assistencia={{
          ...assistencia,
          logo: clearedImages.logo ? '' : assistencia.logo,
          assinatura: clearedImages.assinatura ? '' : assistencia.assinatura,
        }}
        fieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        imageFiles={imageFiles}
        onImageFileSelect={handleImageFileSelect}
        onImageFileClear={handleImageFileClear}
        loading={loading}
        saving={saving}
        isEdit={isEdit}
        canWrite={user?.permission?.assistencias?.write === true}
      />
    </Page>
  );
};

export default AssistenciasAdd;
